/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { Helmet } from 'react-helmet';

import { useQuery } from '@apollo/client';
import { BANNER_AD_PLACEMENTS, BannerAdRowContainer } from '@coursera-web/promo-units';
import { compose, setDisplayName, withProps } from 'recompose';

import type { InjectedRouter } from 'js/lib/connectToRouter';
import connectToRouter from 'js/lib/connectToRouter';
import user from 'js/lib/user';

import { Grid, PageGridContainer } from '@coursera/cds-core';

import { ProductVariantNames } from 'bundles/browse-common/constants';
import type { Subdomain } from 'bundles/browse-common/legacy/components/Domain/types/SubDomain';
import withDegreeAndMastertrackLists from 'bundles/browse-common/legacy/components/hoc/withDegreeAndMastertrackLists';
import type { DiscoveryCollection } from 'bundles/browse-common/legacy/types/DiscoveryCollections';
import type { DegreeListsV3, DegreeProductMember } from 'bundles/browse-common/legacy/types/degree';
import DomainPopularTopics from 'bundles/browse/components/Domain/DomainPopularTopics';
import getDomainPopularTopicsData from 'bundles/browse/components/Domain/constants/domainPopularTopicsData';
import getSubDomainPopularTopicsData from 'bundles/browse/components/Domain/constants/subDomainPopularTopicsData';
import type {
  GetStackableDegreesByDomainForBrowseQuery,
  GetStackableDegreesByDomainForBrowseQueryVariables,
} from 'bundles/browse/components/Domain/queries/__generated__/getStackableDegreesByDomain';
import getStackableDegreesByDomain from 'bundles/browse/components/Domain/queries/getStackableDegreesByDomain.graphql';
import type { Domain } from 'bundles/browse/components/Domain/types/Domain';
import { shouldDomainDisplayMastertrackCollection } from 'bundles/browse/components/Domain/utils/DomainUtils';
import ListGoogleSchemaWithDiscoveryCollections from 'bundles/browse/components/ListGoogleSchemaWithDiscoveryCollection';
import { getDegreeProductFromStackableDegree } from 'bundles/browse/components/utils/StackableDegreesUtils';
import withDiscoveryCollections from 'bundles/browse/components/withDiscoveryCollections';
import { indexToInsertMastertracksInDiscoveryCollections } from 'bundles/browse/utils';
import CollectionCarouselsContainer from 'bundles/collections-carousel/components/carousel/CollectionCarouselsContainer';
import { BROWSE_RESPONSIVE_PROPERTY_BREAKPOINTS, COLLECTION_TYPENAMES } from 'bundles/collections-carousel/constants';
import { mapCollectionsForCarousel } from 'bundles/collections-carousel/utils/mappingUtils';
import CollectionsContainer from 'bundles/collections-common/components/CollectionsContainer';
import GrowthDiscoveryExperiments from 'bundles/epic/clients/GrowthDiscovery';

import _t from 'i18n!nls/browse';

const style = css`
  .bt3-container {
    margin-right: auto;
    margin-left: auto;
    padding-left: 15px;
    padding-right: 15px;

    &::before,
    &::after {
      display: table;
      content: ' ';
    }

    &::after {
      clear: both;
    }
  }

  @media (min-width: 768px) {
    .bt3-container {
      width: 750px;
    }
  }

  @media (min-width: 992px) {
    .bt3-container {
      width: 970px;
    }
  }

  @media (min-width: 1200px) {
    .bt3-container {
      width: 1170px;
    }
  }

  .offerings-wrapper {
    position: relative;
  }
`;

export type InputProps = {
  domain?: Domain;
  subdomain?: Subdomain;
};

type PropsFromRouter = {
  router: InjectedRouter & {
    location: {
      pathname: string;
      query: {
        page?: string;
      };
      protocol?: string;
      hostname?: string;
    };
  };
};

export type GraphqlOptionProps = InputProps & PropsFromRouter;

export type PropsFromGraphql = {
  discoveryCollections: Array<DiscoveryCollection | null>;
  isDiscoveryCollectionsLoading?: boolean;
};

export type Props = PropsFromGraphql &
  GraphqlOptionProps & {
    degreeList: Array<DegreeProductMember>;
    mastertrackList: Array<DegreeProductMember>;
  };

export const ProductOfferings = (props: Props) => {
  const {
    discoveryCollections = [],
    domain,
    subdomain,
    degreeList,
    mastertrackList,
    isDiscoveryCollectionsLoading,
  } = props;

  const renderCanonicalTags = () => {
    const {
      router: {
        location: { hostname, search, pathname, query },
      },
    } = props;

    const queryEntries = Object.keys(query);

    if (queryEntries.length <= 1) {
      return (
        <Helmet>
          <link itemProp="url" rel="canonical" href={`https://${hostname}${pathname}${search}`} />
        </Helmet>
      );
    }

    return null;
  };

  const getPopularTopics = (wantedDomain?: Domain, wantedSubdomain?: Subdomain) => {
    let topicsList;
    if (wantedSubdomain) {
      const popularSubDomainTopics = getSubDomainPopularTopicsData();
      topicsList = popularSubDomainTopics[wantedSubdomain.id];
    } else if (wantedDomain) {
      const popularDomainTopics = getDomainPopularTopicsData();
      topicsList = popularDomainTopics[wantedDomain.id];
    }
    return (
      topicsList && (
        <div css={{ paddingTop: '30px' }}>
          <DomainPopularTopics topicsList={topicsList} />
        </div>
      )
    );
  };

  const replaceMTWithPathwayDegreesCarousel = shouldDomainDisplayMastertrackCollection(domain?.id);

  const { data: stackableDegreesData } = useQuery<
    GetStackableDegreesByDomainForBrowseQuery,
    GetStackableDegreesByDomainForBrowseQueryVariables
  >(getStackableDegreesByDomain, {
    skip: !domain?.id || !replaceMTWithPathwayDegreesCarousel,
    variables: {
      domain: domain?.id || '',
    },
    context: {
      clientName: 'gatewayGql',
    },
    errorPolicy: 'all',
  });

  const stackableDegreesList = stackableDegreesData?.PremiumProductCollections?.getStackableDegreesByDomain?.elements;

  const pathwayDegrees =
    stackableDegreesList?.map((degree) => {
      const newDegree = getDegreeProductFromStackableDegree(degree);
      return {
        degree: newDegree,
        __typename: COLLECTION_TYPENAMES.STACKABLE_DEGREES,
      };
    }) || [];

  // Collection expects a list of named degree objects
  const degrees = degreeList?.map((degree) => ({ degree, __typename: 'DegreeListsV3' }));
  const mastertracks = mastertrackList?.map((degree) => ({
    degree,
    __typename: 'DegreeListsV3',
  }));
  const availableDegreesAndMastertracksInDomain = degrees.concat(mastertracks);

  const allCollections: (DiscoveryCollection | DegreeListsV3 | null)[] = [...discoveryCollections];

  if (mastertrackList?.length >= 4) {
    // GR-23732 reorder to after guided project carousel
    const indexToInsert = indexToInsertMastertracksInDiscoveryCollections(allCollections);
    allCollections.splice(0, 0, {
      entries: degrees,
      __typename: 'DegreeListsV3',
    } as DegreeListsV3);
    // index + 1 because of degree collections
    allCollections.splice(indexToInsert + 1, 0, {
      entries: mastertracks,
      __typename: 'DegreeListV3Mastertrack',
    } as DegreeListsV3);
  } else if (pathwayDegrees?.length >= 4) {
    const indexToInsert = indexToInsertMastertracksInDiscoveryCollections(allCollections);
    allCollections.splice(0, 0, {
      entries: degrees,
      __typename: 'DegreeListsV3',
    } as DegreeListsV3);
    allCollections.splice(indexToInsert + 1, 0, {
      entries: pathwayDegrees,
      collectionName: () => _t('Get a Head Start on Your Degree Today'),
      __typename: 'DegreeListsV3',
    } as DegreeListsV3);
  } else if (availableDegreesAndMastertracksInDomain?.length > 0) {
    allCollections.unshift({
      entries: availableDegreesAndMastertracksInDomain,
      __typename: 'DegreeListsV3',
    } as DegreeListsV3);
  }

  const CollectionComponent =
    GrowthDiscoveryExperiments.get('showEvolveCollection') ||
    GrowthDiscoveryExperiments.get('showEvolveCollectionBrowse')
      ? CollectionsContainer
      : CollectionCarouselsContainer;
  const isEvolvedCollectionsOnDomain = GrowthDiscoveryExperiments.get('showEvolveCollectionBrowse');
  const commonContent = (
    <React.Fragment>
      {allCollections.length > 0 && (
        <CollectionComponent
          collections={mapCollectionsForCarousel(allCollections)}
          isCollectionsLoading={isDiscoveryCollectionsLoading}
          additionalComponents={[
            { component: getPopularTopics(domain, subdomain), indexToInsert: 2 },
            { component: <BannerAdRowContainer placement={BANNER_AD_PLACEMENTS.BROWSE_HOME} />, indexToInsert: 2 },
          ]}
          customResponsiveProperties={BROWSE_RESPONSIVE_PROPERTY_BREAKPOINTS}
        />
      )}
      {!user.isAuthenticatedUser() && (
        <ListGoogleSchemaWithDiscoveryCollections discoveryCollections={discoveryCollections} />
      )}
    </React.Fragment>
  );

  return isEvolvedCollectionsOnDomain ? (
    <PageGridContainer data-e2e="product-offerings" data-testid="ProductOfferings">
      {renderCanonicalTags()}
      <Grid item xs={12}>
        {commonContent}
      </Grid>
    </PageGridContainer>
  ) : (
    <section className="rc-ProductOfferings" data-e2e="product-offerings" data-testid="ProductOfferings" css={style}>
      {renderCanonicalTags()}
      <div className="offerings-wrapper bt3-container body-container">{commonContent}</div>
    </section>
  );
};

export default compose<Props, InputProps>(
  setDisplayName('ProductOfferingHOC'),
  connectToRouter<InputProps & PropsFromRouter, InputProps>((router) => ({ router })),
  withDegreeAndMastertrackLists<InputProps>(({ domain }) => {
    const domains = domain ? [domain.id] : null;
    const productVariants = [
      ProductVariantNames.BachelorsDegree,
      ProductVariantNames.MastersDegree,
      ProductVariantNames.Mastertrack,
    ];
    const replaceMTWithPathwayDegreesCarousel = shouldDomainDisplayMastertrackCollection(domain?.id);
    if (replaceMTWithPathwayDegreesCarousel) productVariants.splice(2, 1);
    return { domains, productVariants, ssr: true };
  }),
  // Props for withDiscoveryCollections.
  withProps(({ subdomain, domain }: InputProps) => {
    const contextType = subdomain ? 'SUBCATEGORY' : 'CATEGORY';
    const contextId = subdomain ? subdomain.id : domain?.id || '';
    const passThroughParameters = [
      {
        name: subdomain ? 'subcategory_id' : 'category_id',
        value: contextId,
      },
    ];

    return { contextType, contextId, passThroughParameters };
  }),
  withDiscoveryCollections()
)(ProductOfferings);
